import { X } from 'lucide-react';
import { useEffect } from 'react';
import type {
  CreatePricingVariantInput,
  MeteringProfileKind,
  ModelPricingRecord,
  ModelProtocolRecord,
  PricingVariantRecord,
} from '@nadoutong/contracts';
import {
  UNIT_VARIANT_KEY_RE,
  VIDEO_INPUT_VARIANT_VALUES,
  isUnitMeteredProfileKind,
  meteringCapabilityForProfileKind,
  resolveStoredMeteringSpec,
  tokenMeteringSpecFrom,
  tokenVariantLookupModeFor,
  variantFieldsInTemplate,
} from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { Button, Chip, FormField, Input, Table, TBody, Td, Th, THead, Tr } from '../ui/index.ts';
import { formatLocalTimestamp } from './formatters.ts';
import {
  MeteringShapeSummary,
  VariantFieldInputs,
  VariantKeyPreview,
  composeVariantKey,
  parseVariantKey,
  type VariantFieldValues,
} from './VariantKeyFields.tsx';

/**
 * The TOKEN-lane price tier surface (#1937) — the operator's read AND write
 * face over `model_pricing_variants`.
 *
 * ── WHY THIS EXISTS AT ALL ────────────────────────────────────────────────
 *
 * #1937 gave the token lane a price-tier axis and landed it in the schema, the
 * submit gate, the rating read and the cost kernel. The ops console was the
 * one seat it did not reach: `listModelPricing` selected `model_pricing_tiers`
 * and nothing else, so the console could not display a tier, and the pricing
 * form built a body carrying neither `tiers` NOR `variants`.
 *
 * That is not merely a missing feature. Variant rows CASCADE from the base row
 * (migration 0038), so a new generation starts with none — and an operator
 * repricing a tiered SKU through this page landed a generation on which no
 * tier had a rate, while `tokenVariantLookupModeFor` (which reads the stored
 * SPEC, not the price list) kept the exact-match rule in force. Every tiered
 * request then rated `variant_price_missing`: delivered, upstream paid,
 * charged nothing.
 *
 * The platform write face now refuses that generation outright
 * (`MODEL_PRICING_VARIANTS_REQUIRED`), which is why this file has to land in
 * the same change: with the guard and without the field, a tiered SKU's price
 * would be uneditable through the console at all.
 *
 * ── SCOPE, STATED RATHER THAN IMPLIED ─────────────────────────────────────
 *
 * The editor covers `variant_key` plus the four rate columns every token SKU
 * prices on — input, output, cache read, cache write — and the reasoning /
 * image-input rates where the model meters them. It deliberately does NOT offer Anthropic's
 * 5m/1h cache-write split per tier: no tiered SKU in service is an Anthropic
 * one (the measured case is an `ark_video_token_v1` video model), the columns are
 * nullable on the table, and adding two more inputs per row to a form that can
 * hold sixteen rows buys nothing today. A generation needing them is still
 * writable through `ops model pricing-add --variants`, whose JSON is the
 * write face's full shape.
 */

/** The ceiling the platform write face refuses above. */
const MAX_PRICING_VARIANTS = 16;

export interface VariantRowDraft {
  readonly variantKey: string;
  /**
   * Per-template-field values (#1883). When the SKU's spec declares a
   * `variant_key_template` these are the operator's inputs and `variantKey`
   * is DERIVED from them through the contracts' own composition rule — the
   * key is never hand-typed. Without a readable template the editor falls
   * back to the free-text key and this stays empty.
   */
  readonly fields: VariantFieldValues;
  readonly input: string;
  readonly output: string;
  readonly cacheRead: string;
  readonly cacheWrite: string;
  readonly reasoning: string;
  readonly imageInput: string;
}

export const EMPTY_VARIANT_ROW: VariantRowDraft = {
  variantKey: '',
  fields: {},
  input: '',
  output: '',
  cacheRead: '',
  cacheWrite: '',
  reasoning: '',
  imageInput: '',
};

/**
 * Does this model declare a TOKEN-lane tier axis?
 *
 * `tokenVariantLookupModeFor` is the derivation — the SAME function the
 * gateway's submit gate scopes its price check by, that platform rating
 * spreads onto the rates, and that the platform write face's own guard reads.
 * A console predicate of its own ("does the spec have a variant_key_template")
 * would be a fourth spelling of a rule that already has one, and it would
 * disagree first on the case that matters least visibly: a stored document
 * that could not be parsed, which the shared rule treats as tiered precisely
 * because whether it declared tiers is what is unknowable.
 *
 * Unit-metered profiles are excluded for the reason the platform guard
 * excludes them: their price list is `model_unit_pricing`, a separate
 * timeline this form does not write.
 *
 * A token profile with a FIXED template (`ark_video_token_v1`) is tiered BY FORM:
 * the profile's own sales shape declares the axis, so the tier editor renders
 * regardless of the stored spec's state. The registration form always stores
 * the fixed-template spec, so a spec-less such row is an out-of-band state to
 * converge, not a "no tiers" choice — there is no such choice on this form.
 */
export function declaresTokenPriceVariants(
  protocols: readonly ModelProtocolRecord[],
): boolean {
  return protocols.some(entryDeclaresTokenTiers);
}

/**
 * Does ONE registration declare token-lane tiers?
 *
 * The per-entry arm of `declaresTokenPriceVariants`, factored out so
 * `baseRateAnchorKind` asks the exact same question with the exact same
 * edge verdicts (an unreadable stored spec counts as tiered) instead of
 * growing a drifting copy. A `null`-profile or unit-metered row answers
 * false — it declares no token tier axis — and each CALLER decides what
 * that absence means for the whole model.
 */
function entryDeclaresTokenTiers(entry: ModelProtocolRecord): boolean {
  if (entry.metering_profile === null) return false;
  if (isUnitMeteredProfileKind(entry.metering_profile)) return false;
  if (
    meteringCapabilityForProfileKind(entry.metering_profile).fixedVariantKeyTemplate !== null
  ) {
    return true;
  }
  return (
    tokenVariantLookupModeFor(resolveStoredMeteringSpec(entry.metering_spec)) ===
    'exact_match_only'
  );
}

/**
 * WHY is the base row of a token pricing generation an anchor rather than a
 * live rate — or is it one at all (`null`)?
 *
 *   - `'tiered_tokens'`: every token-lane registration declares tiers.
 *     Rating then resolves prices by exact tier match (ADR-012 §3), so the
 *     base four buckets price no request — the write face still requires
 *     them because a generation must carry them, i.e. they anchor the
 *     listing.
 *   - `'unit_lane'`: the model's only registrations are unit-metered. Its
 *     money moves through `model_unit_pricing`; the token price list is
 *     never read, so a token history row wearing the "current" badge is
 *     the same misleading anchor by a different route.
 *   - `null`: at least one token-lane registration prices off the base row
 *     (a flat spec, or a `null` profile whose metering is unknowable here),
 *     or there are no registrations at all. No anchor claim may be made.
 *
 * The protocols table is shared across lanes, and that is why the tiered
 * arm is not simply `declaresTokenPriceVariants`: ONE tiered registration
 * makes the tier editor necessary, but a SECOND, flat token registration
 * keeps the base row as ITS live price list — telling the operator "the
 * base rates do not bill" would then be false. Every consumer of this
 * verdict (the form note, the history chip, the de-emphasis) makes that
 * claim, so they all key off this one derivation.
 */
export type BaseRateAnchorKind = 'tiered_tokens' | 'unit_lane';

export function baseRateAnchorKind(
  protocols: readonly ModelProtocolRecord[],
): BaseRateAnchorKind | null {
  let sawTieredTokenLane = false;
  let sawUnitLane = false;
  for (const entry of protocols) {
    if (
      entry.metering_profile !== null &&
      isUnitMeteredProfileKind(entry.metering_profile)
    ) {
      sawUnitLane = true;
      continue;
    }
    if (!entryDeclaresTokenTiers(entry)) return null;
    sawTieredTokenLane = true;
  }
  if (sawTieredTokenLane) return 'tiered_tokens';
  return sawUnitLane ? 'unit_lane' : null;
}

export interface TokenTierAxis {
  readonly template: string;
  readonly profile: MeteringProfileKind;
}

/**
 * The declared tier axis, when one is READABLE.
 *
 * The STORED spec's template comes first, and that precedence is a money
 * rule, not a preference: the gateway resolves each request's tier from the
 * stored document, so the price rows written here must be keyed by the
 * template that document declares — a legacy row whose stored template
 * diverges from the profile's fixed one keeps rendering per its own template
 * until re-registration converges it (the fixed-pair rows would otherwise
 * price keys no request ever resolves).
 *
 * The profile's `fixedVariantKeyTemplate` is the FALLBACK, for a fixed-form
 * profile whose spec is absent or unreadable: the profile's own shape is the
 * only honest structure left, and it replaces the old free-text-key fallback
 * there. Profiles with no fixed template keep it: an unreadable stored
 * document on one of those still shows the free-text key rather than
 * inventing a field structure the spec did not state.
 */
export function tokenTierAxisFrom(
  protocols: readonly ModelProtocolRecord[],
): TokenTierAxis | undefined {
  for (const entry of protocols) {
    if (entry.metering_profile === null) continue;
    if (isUnitMeteredProfileKind(entry.metering_profile)) continue;
    const spec = tokenMeteringSpecFrom(resolveStoredMeteringSpec(entry.metering_spec));
    if (spec !== null) {
      return { template: spec.variant_key_template, profile: entry.metering_profile };
    }
    const fixed = meteringCapabilityForProfileKind(entry.metering_profile)
      .fixedVariantKeyTemplate;
    if (fixed !== null) {
      return { template: fixed, profile: entry.metering_profile };
    }
  }
  return undefined;
}

function rateToDraft(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : '';
}

/** The base row's rate buckets as form draft strings. */
export interface BaseRateDrafts {
  readonly input: string;
  readonly output: string;
  readonly cacheRead: string;
  readonly cacheWrite: string;
  readonly cacheWrite5m: string;
  readonly cacheWrite1h: string;
  readonly reasoning: string;
  readonly imageInput: string;
}

/**
 * Seed the base rate buckets from a generation — the BASE-ROW MIRROR of
 * `variantDraftsFrom` below, closing the gap that half left open.
 *
 * On a SKU where the base rates are anchor-only (`baseRateAnchorKind`),
 * the four buckets are numbers no request is ever priced by — so a
 * new-generation form that demands them hand-typed is a required question
 * the operator cannot answer, and should not have to. Carrying the current
 * generation's values forward makes "same anchor, new tier rates" the path
 * of least resistance; the drafts stay editable, and a model with NO
 * current generation still starts blank, because the first anchor is a
 * real decision someone has to make once.
 *
 * `cacheWrite5m` falls back to the flat cache-write rate exactly as the
 * price-history column renders it: a pre-split generation stated ONE
 * cache-write price, and that price is what "carry the current rates
 * forward" means for the 5m bucket.
 */
export function baseRateDraftsFrom(pricing: ModelPricingRecord): BaseRateDrafts {
  return {
    input: rateToDraft(pricing.input_per_mtok_usd),
    output: rateToDraft(pricing.output_per_mtok_usd),
    cacheRead: rateToDraft(pricing.cache_read_per_mtok_usd),
    cacheWrite: rateToDraft(pricing.cache_write_per_mtok_usd),
    cacheWrite5m: rateToDraft(
      pricing.cache_write_5m_per_mtok_usd ?? pricing.cache_write_per_mtok_usd,
    ),
    cacheWrite1h: rateToDraft(pricing.cache_write_1h_per_mtok_usd),
    reasoning: rateToDraft(pricing.reasoning_per_mtok_usd),
    imageInput: rateToDraft(pricing.image_input_per_mtok_usd),
  };
}

/**
 * Seed the form from a generation's existing tiers.
 *
 * THE ANTI-DATA-LOSS HALF, and the reason it is prefill rather than a blank
 * form plus a warning: repricing a tiered SKU means restating every tier, and
 * an operator who has to retype sixteen keys from a table above will
 * eventually mistype one — which does not fail loudly, it prices a tier
 * nobody's requests resolve to while the tier they DO resolve to has no row.
 * Carrying the keys forward makes the ordinary edit ("same tiers, new rates")
 * the path of least resistance.
 *
 * The RATES are carried too, so an operator changing one tier's price does not
 * have to restate the others. They are a starting point, not a claim: the
 * whole array is submitted and the generation is what it says.
 */
export function variantDraftsFrom(
  variants: readonly PricingVariantRecord[] | undefined,
  template?: string,
): readonly VariantRowDraft[] {
  if (variants === undefined || variants.length === 0) return [EMPTY_VARIANT_ROW];
  return variants.map((variant) => ({
    variantKey: variant.variant_key,
    // Parsed back into the per-field controls so the carried-forward tiers
    // are editable field by field; a key outside the template keeps its raw
    // spelling and the free-text arm of the editor shows it.
    fields:
      template === undefined
        ? {}
        : parseVariantKey(template, variant.variant_key) ?? {},
    input: rateToDraft(variant.input_per_mtok_usd),
    output: rateToDraft(variant.output_per_mtok_usd),
    cacheRead: rateToDraft(variant.cache_read_per_mtok_usd),
    cacheWrite: rateToDraft(variant.cache_write_per_mtok_usd),
    reasoning: rateToDraft(variant.reasoning_per_mtok_usd),
    imageInput: rateToDraft(variant.image_input_per_mtok_usd),
  }));
}

export type VariantValidation =
  | { readonly ok: true; readonly variants: readonly CreatePricingVariantInput[] }
  | { readonly ok: false; readonly key: I18nKey; readonly params?: Record<string, string | number> };

function parseOptionalRate(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return undefined;
  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * The same rules the platform write face refuses with, applied here first —
 * ceiling and charset from the SAME contracts constants it validates against,
 * so an operator meets a readable message instead of a proxied 400.
 *
 * A row whose every field is blank is DROPPED rather than refused: the form
 * always renders at least one row, so a generation that genuinely has no tiers
 * would otherwise be unsubmittable. Dropping empties makes the empty form mean
 * `variants: []`, which is the write face's "this generation prices no tiers"
 * answer — the one an operator retiring the axis needs.
 */
export function validateVariantDrafts(
  rows: readonly VariantRowDraft[],
  usesReasoningRate: boolean,
  usesImageInputRate: boolean,
  template?: string,
  /**
   * `'single_rate'` card shape (`TOKEN_PRICE_CARD_SHAPES`): the editor asks
   * ONE price per tier, and the submitted variant carries it in BOTH
   * input and output with no cache keys — the exact rows operators were
   * hand-equalizing before, so storage and rating see nothing new.
   */
  singleRate = false,
): VariantValidation {
  const populated = rows.filter(
    (row) =>
      row.variantKey.trim().length > 0 ||
      Object.values(row.fields).some((value) => (value ?? '').trim().length > 0) ||
      row.input.trim().length > 0 ||
      row.output.trim().length > 0 ||
      row.cacheRead.trim().length > 0 ||
      row.cacheWrite.trim().length > 0 ||
      row.reasoning.trim().length > 0 ||
      row.imageInput.trim().length > 0,
  );
  if (populated.length > MAX_PRICING_VARIANTS) {
    return {
      ok: false,
      key: 'model_detail.pricing.variants.error.too_many',
      params: { max: MAX_PRICING_VARIANTS },
    };
  }
  const variants: CreatePricingVariantInput[] = [];
  const seen = new Set<string>();
  for (const [index, row] of populated.entries()) {
    const line = index + 1;
    // With a readable template the key is RECOMPOSED from the per-field
    // values through the contracts' own rule, never read from a typed string;
    // an incomplete or out-of-alphabet field refuses with the row named.
    // Without one (free-text fallback), trimmed BEFORE the charset test, and
    // the trimmed value is submitted: the charset admits no whitespace, so
    // trimming can only turn an invalid draft valid, never launder an
    // invalid key.
    const variantKey =
      template === undefined
        ? row.variantKey.trim()
        : composeVariantKey(template, row.fields) ?? '';
    if (template !== undefined && variantKey.length === 0) {
      return {
        ok: false,
        key: 'model_detail.pricing.variants.error.fields_required',
        params: { row: line },
      };
    }
    // The EMPTY key is refused, and it is the one rule here that is not
    // obvious: `''` is the base row, and the base row is the generation
    // itself. A `''` variant row would be a second, competing statement of
    // the same tier that the cost kernel would PREFER.
    if (variantKey.length === 0 || !UNIT_VARIANT_KEY_RE.test(variantKey)) {
      return { ok: false, key: 'model_detail.pricing.variants.error.key_invalid', params: { row: line } };
    }
    if (seen.has(variantKey)) {
      return {
        ok: false,
        key: 'model_detail.pricing.variants.error.duplicate',
        params: { variant: variantKey },
      };
    }
    seen.add(variantKey);
    if (singleRate) {
      // The one price, written into both token buckets; no cache keys, so the
      // write face stores its zero defaults — byte-identical to the rows a
      // chat-shaped submit of "same number twice, caches blank" produced.
      const rate = parseOptionalRate(row.input);
      if (typeof rate !== 'number') {
        return {
          ok: false,
          key: 'model_detail.pricing.variants.error.single_rate_required',
          params: { row: line },
        };
      }
      variants.push({
        variant_key: variantKey,
        input_per_mtok_usd: rate,
        output_per_mtok_usd: rate,
      });
      continue;
    }
    const input = parseOptionalRate(row.input);
    const output = parseOptionalRate(row.output);
    if (typeof input !== 'number' || typeof output !== 'number') {
      return { ok: false, key: 'model_detail.pricing.variants.error.rates_required', params: { row: line } };
    }
    const cacheRead = parseOptionalRate(row.cacheRead);
    const cacheWrite = parseOptionalRate(row.cacheWrite);
    const reasoning = parseOptionalRate(row.reasoning);
    const imageInput = parseOptionalRate(row.imageInput);
    if (cacheRead === null || cacheWrite === null || reasoning === null || imageInput === null) {
      return { ok: false, key: 'model_detail.pricing.variants.error.rate_invalid', params: { row: line } };
    }
    // A variant row REPLACES the base rates wholesale — there is no partial
    // override — so a tiered model that meters reasoning must carry the rate
    // on every tier. Blank would store NULL and drop that tier's thinking
    // tokens to the legacy free path, which is the same silent undercharge
    // the base row's own reasoning check exists to stop.
    if (usesReasoningRate && reasoning === undefined) {
      return {
        ok: false,
        key: 'model_detail.pricing.variants.error.reasoning_required',
        params: { row: line },
      };
    }
    // Same wholesale-replace rule as reasoning: a blank image-input cell
    // stores NULL, and rating bills those tokens at the text input rate.
    if (usesImageInputRate && imageInput === undefined) {
      return {
        ok: false,
        key: 'model_detail.pricing.variants.error.image_input_required',
        params: { row: line },
      };
    }
    variants.push({
      variant_key: variantKey,
      input_per_mtok_usd: input,
      output_per_mtok_usd: output,
      ...(cacheRead !== undefined ? { cache_read_per_mtok_usd: cacheRead } : {}),
      ...(cacheWrite !== undefined ? { cache_write_per_mtok_usd: cacheWrite } : {}),
      ...(reasoning !== undefined ? { reasoning_per_mtok_usd: reasoning } : {}),
      ...(imageInput !== undefined ? { image_input_per_mtok_usd: imageInput } : {}),
    });
  }
  return { ok: true, variants };
}

// ─── read face ───────────────────────────────────────────────────────────

interface TableProps {
  readonly pricing: ModelPricingRecord;
  readonly usesReasoningRate: boolean;
  readonly usesImageInputRate: boolean;
  /** Is this generation the currently-effective one in the price history? */
  readonly isCurrent: boolean;
  /**
   * `'single_rate'` card shape: one price column per tier — the vendor's own
   * card states one figure, and the stored input==output / zero-cache cells
   * are its storage encoding, not five more prices to display.
   */
  readonly singleRate?: boolean;
}

/**
 * One generation's tiers, under its row in the price history.
 *
 * Renders nothing when the generation has none, which is every generation of
 * every untiered SKU — so a token model's price page is unchanged except on
 * the SKUs this dimension actually applies to.
 *
 * The header names the generation the tiers belong to — its `effective_from`,
 * plus the current-generation chip — in the unit card's generation-header
 * shape, because with more than one generation in the history a bare tier
 * table reads as unparented: the operator could not tell which price-history
 * row it restates (owner observation, 2026-08-17).
 */
export function PricingVariantsTable({
  pricing,
  usesReasoningRate,
  usesImageInputRate,
  isCurrent,
  singleRate = false,
}: TableProps): JSX.Element | null {
  const t = useT();
  const variants = pricing.variants ?? [];
  if (variants.length === 0) return null;
  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`model-pricing-variants-${pricing.effective_from}`}
    >
      <div className="flex items-center gap-3">
        <span className="text-label text-paper-mid">
          {t('model_detail.pricing.variants.title')}
        </span>
        <span className="font-mono text-dense text-paper-mid">
          {formatLocalTimestamp(pricing.effective_from, { seconds: true })}
        </span>
        {isCurrent ? (
          <Chip
            tone="signal"
            testId={`model-pricing-variants-current-${pricing.effective_from}`}
          >
            {t('model_detail.pricing.variants.generation_current')}
          </Chip>
        ) : null}
      </div>
      <Table testId={`model-pricing-variants-table-${pricing.effective_from}`}>
        <THead>
          <Tr interactive={false}>
            <Th>{t('model_detail.pricing.variants.column.variant')}</Th>
            {singleRate ? (
              <Th num>{t('model_detail.pricing.column.single_rate')}</Th>
            ) : (
              <>
                <Th num>{t('model_detail.pricing.column.input')}</Th>
                <Th num>{t('model_detail.pricing.column.output')}</Th>
                <Th num>{t('model_detail.pricing.column.cache_read')}</Th>
                <Th num>{t('model_detail.pricing.column.cache_write')}</Th>
                {usesReasoningRate ? (
                  <Th num>{t('model_detail.pricing.column.reasoning')}</Th>
                ) : null}
                {usesImageInputRate ? (
                  <Th num>{t('model_detail.pricing.column.image_input')}</Th>
                ) : null}
              </>
            )}
          </Tr>
        </THead>
        <TBody>
          {variants.map((variant) => (
            <Tr
              key={variant.variant_key}
              data-testid={`model-pricing-variant-row-${variant.variant_key}`}
            >
              <Td mono>{variant.variant_key}</Td>
              {singleRate ? (
                // The one price, read off the stored input bucket (its
                // storage home; output is the same figure by the write rule).
                <Td num emphasis>{variant.input_per_mtok_usd}</Td>
              ) : (
                <>
                  <Td num emphasis>{variant.input_per_mtok_usd}</Td>
                  <Td num emphasis>{variant.output_per_mtok_usd}</Td>
                  <Td num>{variant.cache_read_per_mtok_usd}</Td>
                  <Td num>{variant.cache_write_per_mtok_usd}</Td>
                  {usesReasoningRate ? (
                    <Td num>{variant.reasoning_per_mtok_usd ?? '-'}</Td>
                  ) : null}
                  {usesImageInputRate ? (
                    <Td num>{variant.image_input_per_mtok_usd ?? '-'}</Td>
                  ) : null}
                </>
              )}
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

// ─── write face ──────────────────────────────────────────────────────────

interface EditorProps {
  readonly rows: readonly VariantRowDraft[];
  readonly onChange: (next: readonly VariantRowDraft[]) => void;
  readonly usesReasoningRate: boolean;
  readonly usesImageInputRate: boolean;
  readonly disabled: boolean;
  /**
   * The spec's `variant_key_template`, when readable (#1883). With it the
   * key column becomes per-field controls and the key itself is derived;
   * without it the free-text key input stays exactly as it was.
   */
  readonly template?: string | undefined;
  /** The profile carrying the axis, for the read-only shape summary. */
  readonly profile?: MeteringProfileKind | undefined;
  /**
   * `'single_rate'` card shape: one price input per tier. The draft's
   * `input` field is its home — `validateVariantDrafts` writes it into both
   * token buckets on submit — so seeding from a stored generation's input
   * bucket round-trips without a special case.
   */
  readonly singleRate?: boolean;
}

export function PricingVariantsEditor({
  rows,
  onChange,
  usesReasoningRate,
  usesImageInputRate,
  disabled,
  template,
  profile,
  singleRate = false,
}: EditorProps): JSX.Element {
  const t = useT();

  function updateRow(index: number, patch: Partial<VariantRowDraft>): void {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  const rateFields: readonly {
    readonly id: 'input' | 'output' | 'cacheRead' | 'cacheWrite' | 'reasoning' | 'imageInput';
    readonly labelKey: I18nKey;
  }[] = singleRate
    ? [{ id: 'input', labelKey: 'model_detail.pricing.column.single_rate' }]
    : [
        { id: 'input', labelKey: 'model_detail.pricing.column.input' },
        { id: 'output', labelKey: 'model_detail.pricing.column.output' },
        { id: 'cacheRead', labelKey: 'model_detail.pricing.column.cache_read' },
        { id: 'cacheWrite', labelKey: 'model_detail.pricing.column.cache_write' },
        ...(usesReasoningRate
          ? ([{ id: 'reasoning', labelKey: 'model_detail.pricing.column.reasoning' }] as const)
          : []),
        ...(usesImageInputRate
          ? ([{ id: 'imageInput', labelKey: 'model_detail.pricing.column.image_input' }] as const)
          : []),
      ];

  const templateFields = template === undefined ? [] : variantFieldsInTemplate(template);
  // The one-field video template renders the CLOSED PAIR as two fixed rows:
  // the keys are constants (`VIDEO_INPUT_VARIANT_VALUES`), so there is
  // nothing to select and nothing to add — the operator only fills prices.
  const fixedVideoPair =
    template !== undefined &&
    templateFields.length === 1 &&
    templateFields[0] === 'has_video_input';
  const fixedKeys: readonly string[] = fixedVideoPair
    ? [
        composeVariantKey(template, {
          has_video_input: VIDEO_INPUT_VARIANT_VALUES.absent,
        }) ?? VIDEO_INPUT_VARIANT_VALUES.absent,
        composeVariantKey(template, {
          has_video_input: VIDEO_INPUT_VARIANT_VALUES.present,
        }) ?? VIDEO_INPUT_VARIANT_VALUES.present,
      ]
    : [];

  useEffect(() => {
    if (!fixedVideoPair) return;
    if (rows.length === 2 && rows.every((row, i) => row.variantKey === fixedKeys[i])) {
      return;
    }
    onChange(
      fixedKeys.map((key, i) => {
        const existing = rows.find((row) => row.variantKey === key);
        return (
          existing ?? {
            ...EMPTY_VARIANT_ROW,
            variantKey: key,
            fields: {
              has_video_input:
                i === 0
                  ? VIDEO_INPUT_VARIANT_VALUES.absent
                  : VIDEO_INPUT_VARIANT_VALUES.present,
            },
          }
        );
      }),
    );
    // `fixedKeys` is derived from `template`, which stands in for it here.
  }, [fixedVideoPair, rows, onChange, template]);

  function updateFields(index: number, values: VariantFieldValues): void {
    if (template === undefined) return;
    updateRow(index, { fields: values, variantKey: composeVariantKey(template, values) ?? '' });
  }

  const videoLabelFor = (key: string): string =>
    key === fixedKeys[0]
      ? t('metering.video_input.absent')
      : t('metering.video_input.present');

  return (
    <div className="flex flex-col gap-3" data-testid="model-pricing-variants-editor">
      <span className="text-label text-paper-mid">
        {t('model_detail.pricing.variants.add.title')}
      </span>
      {template !== undefined && profile !== undefined ? (
        <MeteringShapeSummary
          profile={profile}
          template={template}
          testId="model-pricing-variants-shape"
        />
      ) : null}
      <p className="m-0 text-micro text-paper-low" data-testid="model-pricing-variants-hint">
        {t(
          template === undefined
            ? 'model_detail.pricing.variants.add.hint'
            : 'model_detail.pricing.variants.add.hint_derived',
        )}
      </p>
      {rows.map((row, index) => (
        <div
          // Index keys are safe here: rows are only appended/removed by
          // explicit operator action and carry no identity of their own.
          key={index}
          className="flex flex-col gap-2"
          data-testid={`model-pricing-variant-draft-${index}`}
        >
          <div className="flex flex-wrap items-end gap-3">
            {fixedVideoPair ? (
              <div className="flex min-w-[10rem] flex-col gap-1.5">
                <span className="text-label text-paper-mid">
                  {t('model_detail.pricing.variants.column.variant')}
                </span>
                <span
                  className="text-body text-paper"
                  data-testid={`model-pricing-variant-${index}-fixed-key`}
                >
                  {videoLabelFor(row.variantKey)}
                  <span className="ml-1 font-mono text-micro text-paper-low">
                    {row.variantKey}
                  </span>
                </span>
              </div>
            ) : template !== undefined ? (
              <VariantFieldInputs
                template={template}
                values={row.fields}
                onChange={(values) => updateFields(index, values)}
                disabled={disabled}
                idPrefix={`model-pricing-variant-${index}`}
              />
            ) : (
              <FormField
                label={t('model_detail.pricing.variants.add.key.label')}
                fieldId={`model-pricing-variant-${index}-variantKey`}
                className="min-w-[10rem] flex-1"
              >
                <Input
                  id={`model-pricing-variant-${index}-variantKey`}
                  type="text"
                  mono
                  value={row.variantKey}
                  onChange={(e) => updateRow(index, { variantKey: e.currentTarget.value })}
                  data-testid={`model-pricing-variant-${index}-variantKey`}
                  disabled={disabled}
                />
              </FormField>
            )}
            {rateFields.map((field) => (
              <FormField
                key={field.id}
                label={t(field.labelKey)}
                fieldId={`model-pricing-variant-${index}-${field.id}`}
                className="min-w-[7rem] flex-1"
              >
                <Input
                  id={`model-pricing-variant-${index}-${field.id}`}
                  type="number"
                  mono
                  min="0"
                  step="0.000001"
                  value={row[field.id]}
                  onChange={(e) => updateRow(index, { [field.id]: e.currentTarget.value })}
                  data-testid={`model-pricing-variant-${index}-${field.id}`}
                  disabled={disabled}
                />
              </FormField>
            ))}
            {fixedVideoPair ? null : (
              <button
                type="button"
                onClick={() => onChange(rows.filter((_, i) => i !== index))}
                disabled={disabled || rows.length === 1}
                aria-label={t('model_detail.pricing.variants.add.remove_aria', { row: index + 1 })}
                data-testid={`model-pricing-variant-${index}-remove`}
                className="mb-1 inline-flex h-7 w-7 items-center justify-center rounded-full text-paper-low transition-colors duration-120 hover:text-bad focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 disabled:opacity-40"
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </button>
            )}
          </div>
          {template !== undefined && !fixedVideoPair ? (
            <VariantKeyPreview
              template={template}
              values={row.fields}
              testId={`model-pricing-variant-${index}-key-preview`}
            />
          ) : null}
        </div>
      ))}
      {fixedVideoPair ? null : (
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onChange([...rows, EMPTY_VARIANT_ROW])}
            disabled={disabled || rows.length >= MAX_PRICING_VARIANTS}
            data-testid="model-pricing-variants-add-row"
          >
            {t('model_detail.pricing.variants.add.add_row')}
          </Button>
          <span className="text-micro text-paper-low">
            {t('model_detail.pricing.variants.add.row_count', {
              count: rows.length,
              max: MAX_PRICING_VARIANTS,
            })}
          </span>
        </div>
      )}
    </div>
  );
}
